/**
 * Every feature-gate object the chrome layer reads is declared here; adding one is an entry here
 * plus a call site there. With no envelope published, every entry resolves to `defaultValue`.
 *
 * Kinds mirror the `initialize` response sections: `gate` is a boolean, `dynamic-config` a parameter
 * bag, `experiment` a parameter bag that also needs exposure relayed from an engaged surface. There
 * is no `layer` kind, because nothing reads `layer_configs` and layers log exposure per parameter
 * rather than per assignment. Build-time constants do not belong here.
 */

export type Kind = 'gate' | 'dynamic-config' | 'experiment';

/**
 * What the chrome layer does with an envelope older than `maxAgeMs`.
 * - `lastKnown`: keep using the stored value. Right where a stale "on" is harmless.
 * - `registryDefault`: fall back to `defaultValue`. Right where stale must fail safe.
 */
export type StalePolicy = 'lastKnown' | 'registryDefault';

/** The value types a dynamic config or experiment parameter may take. */
export type ParameterValue = string | number | boolean | ReadonlyArray<string>;

export type ConfigParameters = Readonly<Record<string, ParameterValue>>;

interface BaseDefinition {
	/** The Statsig key. Call sites use this exact string literal, never an alias. */
	readonly key: string;
	readonly kind: Kind;
	readonly onStale: StalePolicy;
	/** Age after which the envelope counts as stale for this entry. Defaults to {@link DEFAULT_MAX_AGE_MS}. */
	readonly maxAgeMs?: number;
	readonly owner: string;
	readonly description: string;
}

export interface GateDefinition extends BaseDefinition {
	readonly kind: 'gate';
	/** Used when there is no envelope, the envelope is rejected, or the entry is stale with `registryDefault`. */
	readonly defaultValue: boolean;
}

export interface ConfigDefinition extends BaseDefinition {
	readonly kind: 'dynamic-config' | 'experiment';
	/**
	 * One entry per parameter the chrome layer reads. Doubles as the fallback per parameter: the
	 * cold client only resolves the parameters listed here, each with its own default.
	 */
	readonly defaultValue: ConfigParameters;
}

export type Definition = GateDefinition | ConfigDefinition;

export type Registry = Readonly<Record<string, Definition>>;

/** 14 days. The envelope only refreshes while a frontend surface is mounted, so this is generous on purpose. */
export const DEFAULT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Shipped empty for a long time on purpose, so that branch changed no production behaviour. Two
 * keys were tried and removed before this one: `platform-dst-popper-consolidation` (would swap the
 * popper implementation, and which React hook runs, for anyone with an envelope) and
 * `rovo-ext_sbs_enabled` (its only consumer hardcodes `true`). Tests inject `TEST_REGISTRY` instead
 * of relying on this.
 */
export const REGISTRY = {
	rovo_extension_right_click_menu_code_search: {
		key: 'rovo_extension_right_click_menu_code_search',
		kind: 'gate',
		// https://switcheroo.atlassian.com/ui/gates/0372651c-9063-4185-a8eb-1d82f57a60d5/key/rovo_extension_right_click_menu_code_search
		defaultValue: false,
		// A stale "on" would keep showing a menu item that may have been intentionally killed.
		onStale: 'registryDefault',
		owner: 'Rovo Expansion - Asimo',
		description:
			'Shows the "Search in Code Search app" right-click menu item on selected text, which opens' +
			' the Code Search app in a new tab with the selection prefilled as the query.',
	},
	'proactive-context-menu-killswitch': {
		key: 'proactive-context-menu-killswitch',
		kind: 'gate',
		// https://switcheroo.atlassian.com/ui/gates/68202416-e71b-4269-b7fe-03fd3bea4416/key/proactive-context-menu-killswitch
		defaultValue: false,
		// A stale "on" (still killed) is the safe direction to be stuck in, so keep whatever was last
		// fetched rather than snapping back to the registry default.
		onStale: 'lastKnown',
		owner: 'Rovo Expansion - Asimo',
		description:
			'Emergency kill switch for the proactive context menu overlay (selection tracking, highlight' +
			' menu, right-click menu). When true, the overlay never mounts, regardless of experiment' +
			' assignment.',
	},
	'rovo-ext_companion_refresh': {
		key: 'rovo-ext_companion_refresh',
		kind: 'experiment',
		defaultValue: { isEnabled: false },
		// A stale envelope falls back to control, the Companion's existing behaviour.
		onStale: 'registryDefault',
		owner: 'Rovo Expansion - Asimo',
		description:
			'Rovo Companion default behaviour. When `isEnabled` is true, the Companion starts tucked' +
			' once, sits 12px off the screen edges, its hover area is the widget itself, its drag' +
			' handle, tuck and options buttons move into a collapsible action bar, and it has the Rovo' +
			' button colours. Read through `rovo-companion/hooks/useCompanionRefresh.ts`, which also' +
			' records the exposure when the Companion shows.',
	},
} as const satisfies Registry;

/** The shipped registry as a type, so consumers can index it without spelling `typeof REGISTRY`. */
export type DefaultRegistry = typeof REGISTRY;

export type RegisteredKey = keyof DefaultRegistry;

export const REGISTERED_KEYS: readonly RegisteredKey[] = Object.freeze(
	Object.keys(REGISTRY) as RegisteredKey[],
);

/** The frozen value the chrome layer holds for one definition: a boolean for a gate, a parameter map otherwise. */
export type ResolvedValue<D extends Definition> = D extends GateDefinition
	? boolean
	: { readonly [P in keyof D['defaultValue']]: ParameterValue };

export type ResolvedValues<R extends Registry = typeof REGISTRY> = {
	readonly [K in keyof R]: ResolvedValue<R[K]>;
};

/**
 * The registered keys of one kind. Lets each reader accept only the keys it can answer, so
 * `getFrozenGate('some-experiment')` is a compile error rather than a runtime surprise.
 */
export type KeysOfKind<R extends Registry, K extends Kind> = {
	readonly [P in keyof R]: R[P]['kind'] extends K ? P : never;
}[keyof R];

export type GateKey<R extends Registry = typeof REGISTRY> = KeysOfKind<R, 'gate'>;
export type DynamicConfigKey<R extends Registry = typeof REGISTRY> = KeysOfKind<
	R,
	'dynamic-config'
>;
export type ExperimentKey<R extends Registry = typeof REGISTRY> = KeysOfKind<R, 'experiment'>;

export function isRegistered(key: string): key is RegisteredKey {
	return Object.prototype.hasOwnProperty.call(REGISTRY, key);
}

export function getDefinition(key: string): Definition | undefined {
	return isRegistered(key) ? REGISTRY[key] : undefined;
}

/**
 * Default for a boolean read through `fg()`. Unknown keys and non-gate entries resolve to `false`:
 * an unregistered gate is never on, and a parameter bag is never a boolean.
 */
export function getGateDefault(key: string): boolean {
	const definition = getDefinition(key);
	return definition?.kind === 'gate' ? definition.defaultValue : false;
}

/** A frozen map of every registered entry at its default value. */
export function getRegistryDefaults(): ResolvedValues;
export function getRegistryDefaults<R extends Registry>(registry: R): ResolvedValues<R>;
export function getRegistryDefaults(registry: Registry = REGISTRY): ResolvedValues<Registry> {
	const defaults: Record<string, ResolvedValue<Definition>> = {};
	for (const [key, definition] of Object.entries(registry)) {
		defaults[key] =
			definition.kind === 'gate'
				? definition.defaultValue
				: Object.freeze({ ...definition.defaultValue });
	}
	return Object.freeze(defaults);
}
